// BarGraph2.js
import React from "react";

export const BarGraph5 = ({ data }) => {
  if (!data) return null;

  const {
    caption = "",
    title = "",
    xAxis = { label: "", categories: [] },
    yAxis = { label: "", ticks: [] },
    bars = [],
    colors = []
  } = data;

  // 1) y축 스케일: y=96 (최댓값) ~ y=296 (값=0)
  const yTop = 96;
  const yBottom = 296;
  const chartHeight = yBottom - yTop; // 200

  // 최대값 파악 (ticks 첫 값이 최대라는 가정이 있으면 그대로 사용 가능)
  // 예: [550,500,450,...,0] 이면 maxData = 550
  const maxData = yAxis.ticks[0] || 550;

  // 2) X축 좌표: (예시) xLeft=116.85, xRight=385
  // 가로폭 = 385 - 116.85 = 약 268
  const xLeft = 116.85;
  const xRight = 385;
  const chartWidth = xRight - xLeft;

  // bars.length = 3개
  // 막대 폭(barWidth)와 막대 사이 간격(spacing) 정하기
  const barWidth = 40;
  const spacing = (chartWidth - barWidth * bars.length) / (bars.length + 1);

  // y축 변환: value -> y좌표
  // value=0 => y=296, value=maxData => y=96
  //   y = yBottom - (value / maxData) * chartHeight
  const scaleY = (value) => yBottom - (value / maxData) * chartHeight;

  // 실제 <rect>요소를 만든다
  const renderBars = () => {
    return bars.map((bar, i) => {
      const { robot, value } = bar;

      // x 위치: 왼쪽부터 spacing만큼 띄우고, 막대 하나 + spacing 반복
      const xPos = xLeft + spacing * (i + 1) + barWidth * i;

      // y, height 계산
      const barTop = scaleY(value);      // 막대 상단 y
      const barHeight = yBottom - barTop; // 막대 높이

      // 색상 (i번째 막대에 i번째 color 할당)
      const barColor = colors[i] || "#666666";

      return (
        <rect
          key={`bar-${i}`}
          x={xPos}
          y={barTop}
          width={barWidth}
          height={barHeight}
          fill={barColor}
          stroke="#000"
          strokeWidth="0.9"
        />
      );
    });
  };

  // yAxis, xAxis 선/라벨 그리기
  // (여기서는 최소한의 코드만 남김)
  const renderYAxis = () => (
    <g>
      {/* y축 라벨 */}
      <text
        fill="#000"
        fontFamily="Crimson Text"
        fontSize="19.84"
        textAnchor="middle"
        transform="translate(24 196) rotate(-90)"
      >
        {yAxis.label}
      </text>
      {/* y축 메인 선 */}
      <line
        fill="none"
        stroke="#000"
        strokeLinecap="round"
        strokeWidth="0.9"
        x1={xLeft}
        x2={xLeft}
        y1={yTop}
        y2={yBottom}
      />
      {/* 수평 눈금선 + 틱값 레이블 */}
      {yAxis.ticks.map((tick) => {
        const yPos = scaleY(tick);
        return (
          <g key={`y-tick-${tick}`}>
            <line
              fill="none"
              stroke="#000"
              strokeLinecap="round"
              strokeWidth="0.9"
              x1={xLeft}
              x2={385}
              y1={yPos}
              y2={yPos}
            />
            <line
              fill="none"
              stroke="#000"
              strokeLinecap="round"
              strokeWidth="0.9"
              x1={xLeft - 6}
              x2={xLeft}
              y1={yPos}
              y2={yPos}
            />
            <text
              fill="#000"
              fontFamily="Crimson Text"
              fontSize="19.84"
              textAnchor="end"
              transform={`translate(${xLeft - 12} ${yPos + 6})`}
            >
              {tick}
            </text>
          </g>
        );
      })}
    </g>
  );

  const renderXAxis = () => (
    <g>
      {/* x축 선 */}
      <line
        fill="none"
        stroke="#000"
        strokeLinecap="round"
        strokeWidth="0.9"
        x1={xLeft}
        x2={385}
        y1={yBottom}
        y2={yBottom}
      />
      <line
        fill="none"
        stroke="#000"
        strokeLinecap="round"
        strokeWidth="0.9"
        x1={xLeft - 6}
        x2={xLeft}
        y1={yBottom}
        y2={yBottom}
      />
      {/* x축 라벨 */}
      <text
        fill="#000"
        fontFamily="Crimson Text"
        fontSize="19.84"
        textAnchor="middle"
        transform="translate(250.92434692382812 349.68)"
      >
        {xAxis.label}
      </text>
      {/* 각 로봇명 (Robot A, B, C)를 x축 아래 표시 */}
      {xAxis.categories.map((cat, i) => {
        // 동일 로직으로 xPos 계산
        const xPos = xLeft + spacing * (i + 1) + barWidth * i + barWidth / 2;
        // yPos = 315.84 (예시)
        return (
          <text
            key={`x-cat-${cat}`}
            fill="#000"
            fontFamily="Crimson Text"
            fontSize="19.84"
            textAnchor="middle"
            transform={`translate(${xPos} 315.84)`}
          >
            {cat}
          </text>
        );
      })}
    </g>
  );

  const renderTitle = () => (
    <text
      fill="#000"
      fontFamily="Crimson Text"
      fontSize="19.84"
      textAnchor="middle"
      transform="translate(250.92434692382812 24)"
    >
      {title}
    </text>
  );

  // 범례 (colors 순서대로) -> AI JSON은 "colors" : [ "#666666", "#CCCCCC", "#000000" ]
  //  하지만 실제 "로봇별"로 색을 다르게 표시하고 싶다면 아래와 같이 구현
  //  (혹은 "로봇 A/B/C"를 legend에 표시하고 싶다면 xAxis.categories랑 매칭)
  const renderLegend = () => {
    if (!colors || !colors.length) return null;
    // 임의로 "Robot A, Robot B, Robot C" 또는 bars[i].robot를 legend label로 쓸 수도 있음
    // 여기서는 간단히 3개 색상만 "grass cover" 등 고정 텍스트 대신, bars의 robot 이름 사용 가능
    return (
      <g>
        {/* 범례 박스 */}
        <rect
          fill="none"
          x="141.25286865234375"
          y="366.84"
          width="132.4942626953125"
          height="103"
          stroke="#000"
          strokeLinejoin="mitre"
          strokeWidth="0.9"
        />
        {bars.map((bar, i) => {
          const itemX = 148.25;
          const itemY = 378.84 + i * 32; // 각 범례 항목 간격
          const color = colors[i] || "#666666";
          return (
            <g key={`legend-${bar.robot}`}>
              <rect
                fill={color}
                x={itemX}
                y={itemY}
                width="12"
                height="12"
                stroke="#000"
                strokeLinejoin="mitre"
                strokeWidth="0.9"
              />
              <text
                fill="#000"
                fontFamily="Crimson Text"
                fontSize="19.84"
                textAnchor="top"
                transform={`translate(${itemX + 22} ${itemY + 12})`}
              >
                {bar.robot}
              </text>
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <figure className="image">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="400"
        height="480.84"
        viewBox="0 0 400 480.84"
        role="img"
        aria-label={caption}
      >
        <g id="ed420550-79eb-48d4-af01-cd27cdd08afd" data-name="Layer 1">
          <defs>
            <pattern
              id="bar4"
              x="0"
              y="0"
              width="10"
              height="100"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(50)"
            >
              <rect x="0" y="0" width="5" height="100" fill="#CDCDCD" />
              <rect x="5" y="0" width="5" height="100" fill="#444444" />
            </pattern>
          </defs>

          {/* Y축 */}
          {renderYAxis()}
          {/* X축 */}
          {renderXAxis()}

          {/* 막대들 */}
          {renderBars()}

          {/* 그래프 제목 */}
          {renderTitle()}

          {/* 범례 */}
          {renderLegend()}
        </g>
      </svg>
    </figure>
  );
};

export default BarGraph5;
